/**
 * Centralized Session Management Service
 *
 * Single source of truth for all session-related logic and constants.
 * This eliminates duplication and provides a clear API for session operations.
 */

import { SessionAnalytics } from "./SessionAnalytics";

export const SESSION_CONSTANTS = {
  FULL_LOGOUT_AFTER: 30 * 60 * 1000, // 30 minutes
  PARTIAL_LOGOUT_AFTER: 14 * 60 * 1000, // 14 minutes
  LOGIN_COOLDOWN_PERIOD: 15 * 1000, // 15 seconds
  ACCESS_CHECK_DEBOUNCE: 15 * 1000, // 15 seconds
  ACCESS_CHECK_FOCUS_DEBOUNCE: 5 * 60 * 1000, // 5 minutes for focus-based checks
} as const;

export interface SessionState {
  lastLoginTimestamp: number | null;
  sessionWarningShown: boolean;
  isLoggedIn: boolean;
}

export class SessionService {
  /**
   * Check if user is within login cooldown period
   */
  static isInCooldown(lastLoginTimestamp: number | null): boolean {
    if (!lastLoginTimestamp) return false;

    const now = Date.now();
    const timeSinceLogin = now - lastLoginTimestamp;
    return timeSinceLogin <= SESSION_CONSTANTS.LOGIN_COOLDOWN_PERIOD;
  }

  /**
   * Check if session should trigger full logout
   */
  static shouldFullLogout(inactivityDuration: number): boolean {
    return inactivityDuration >= SESSION_CONSTANTS.FULL_LOGOUT_AFTER;
  }

  /**
   * Check if session should show re-auth modal
   */
  static shouldShowReAuthModal(
    inactivityDuration: number,
    sessionWarningShown: boolean,
  ): boolean {
    return (
      inactivityDuration >= SESSION_CONSTANTS.PARTIAL_LOGOUT_AFTER &&
      !sessionWarningShown
    );
  }

  /**
   * Check if 401 should trigger auth modal
   */
  static shouldShowModalFor401(sessionState: SessionState): boolean {
    const { lastLoginTimestamp, sessionWarningShown, isLoggedIn } =
      sessionState;
    const timeSinceLogin = lastLoginTimestamp
      ? Date.now() - lastLoginTimestamp
      : undefined;

    // Track 401 received for analytics
    if (isLoggedIn && lastLoginTimestamp) {
      // TODO: Pass endpoint name
      SessionAnalytics.track401Received("api_request", timeSinceLogin);
    }

    const shouldShow =
      isLoggedIn &&
      !this.isInCooldown(lastLoginTimestamp) &&
      !sessionWarningShown;

    // Track modal decision
    if (isLoggedIn) {
      if (shouldShow) {
        SessionAnalytics.trackModalShown("401", timeSinceLogin);
      } else {
        const skipReason = this.isInCooldown(lastLoginTimestamp)
          ? "cooldown"
          : sessionWarningShown
            ? "already_shown"
            : "not_logged_in";
        SessionAnalytics.trackModalSkipped(skipReason, timeSinceLogin);
      }
    }

    return shouldShow;
  }

  /**
   * Check if access check should be debounced
   */
  static shouldDebounceAccessCheck(lastCheckTime: number): boolean {
    const now = Date.now();
    return now - lastCheckTime < SESSION_CONSTANTS.ACCESS_CHECK_DEBOUNCE;
  }

  /**
   * Check if focus-based access check should be debounced (5 minutes)
   */
  static shouldDebounceFocusAccessCheck(lastCheckTime: number): boolean {
    const now = Date.now();
    return now - lastCheckTime < SESSION_CONSTANTS.ACCESS_CHECK_FOCUS_DEBOUNCE;
  }

  /**
   * Get session status for debugging
   */
  static getDebugInfo(
    sessionState: SessionState,
    inactivityDuration?: number,
  ): {
    timeSinceLogin: number;
    inCooldown: boolean;
    shouldFullLogout: boolean;
    shouldShowReAuth: boolean;
  } {
    const now = Date.now();
    const timeSinceLogin = sessionState.lastLoginTimestamp
      ? now - sessionState.lastLoginTimestamp
      : Infinity;
    const inCooldown = this.isInCooldown(sessionState.lastLoginTimestamp);

    return {
      timeSinceLogin,
      inCooldown,
      shouldFullLogout: inactivityDuration
        ? this.shouldFullLogout(inactivityDuration)
        : false,
      shouldShowReAuth: inactivityDuration
        ? this.shouldShowReAuthModal(
            inactivityDuration,
            sessionState.sessionWarningShown,
          )
        : false,
    };
  }

  /**
   * Initialize session analytics
   */
  static initializeSession(userId?: string): void {
    SessionAnalytics.initialize(userId);
  }

  /**
   * Track login event
   */
  static trackLogin(source: "initial" | "reauth", userId?: string): void {
    SessionAnalytics.trackLogin(source, userId);
  }

  /**
   * Track logout event
   */
  static trackLogout(reason: "user_action" | "timeout" | "401"): void {
    SessionAnalytics.trackLogout(reason);
  }

  /**
   * Track modal shown due to inactivity
   */
  static trackInactivityModal(inactivityDuration: number): void {
    const timeSinceLogin = SessionAnalytics.getSessionInfo().sessionId
      ? Date.now()
      : undefined;
    SessionAnalytics.trackModalShown(
      "inactivity",
      timeSinceLogin,
      inactivityDuration,
    );
  }
}
